import { DEFAULT_IS_COOLED, DEFAULT_IS_HEATED } from '../../consts';
import { CalculationSummary } from '../../models';
import { Level, RoomListItem, RoomType, SetRoomsEvent } from '../../models';

export interface RbrValidationResult {
    isValid: boolean;
    isPlanEmpty: boolean;
    roomAreasExceedLivingArea: boolean;
    roomIdsWithMissingArea: string[];
}

export const validateBuilding = (levels: Level[] | null, calculationSummary: CalculationSummary | undefined): RbrValidationResult => {
    const rooms = (levels ?? []).flatMap(level => level.roomsData);

    const isPlanEmpty = rooms.length === 0;
    const roomIdsWithMissingArea = rooms.filter(room => room.area == null).map(room => room.id);
    const roomAreasExceedLivingArea = Boolean(
        calculationSummary && calculationSummary.totalLivingArea > 0 && calculationSummary.leftoverArea < 0
    );

    const isValid = !isPlanEmpty && roomIdsWithMissingArea.length === 0 && !roomAreasExceedLivingArea;

    return { isValid, isPlanEmpty, roomAreasExceedLivingArea, roomIdsWithMissingArea };
};

export const getNextUpperLevelNumber = (levels: Level[]): number => {
    return levels.length ? Math.max(...levels.map(level => level.number)) + 1 : 0;
};

export const createUpperLevel = (levels: Level[], newLevelId: string, levelNumber: number, levelName: string): Level[] => {
    const newLevel: Level = { id: newLevelId, number: levelNumber, name: levelName, roomsData: [] };

    const updatedLevelsArray = [...levels];
    updatedLevelsArray.unshift(newLevel);

    return updatedLevelsArray;
};

export const getNextLowerLevelNumber = (levels: Level[]): number => {
    return levels.length ? Math.min(...levels.map(level => level.number)) - 1 : 0;
};

export const createLowerLevel = (levels: Level[], newLevelId: string, levelNumber: number, levelName: string): Level[] => {
    const newLevel: Level = { id: newLevelId, number: levelNumber, name: levelName, roomsData: [] };

    const updatedLevelsArray = [...levels];
    updatedLevelsArray.push(newLevel);

    return updatedLevelsArray;
};

export const setRoomsToLevel = (
    levels: Level[],
    event: SetRoomsEvent,
    createRoomId: () => string,
    createRoomName: (type: RoomType) => string,
    defaultRoomHeight: number
): Level[] | null => {
    const targetLevel = levels.find(level => level.id === event.levelId);

    if (!targetLevel) {
        return null;
    }

    const targetRoomCounts = event.rooms;

    // 1. Prepare empty map of rooms:
    // {
    //     BEDROOM: [],
    //     BATHROOM: [],
    //     KITCHEN: [],
    //     [...]
    // }
    const roomsByType: Record<RoomType, RoomListItem[]> = Object.values(RoomType).reduce(
        (acc, roomType) => ({ ...acc, [roomType]: [] }),
        {} as Record<RoomType, RoomListItem[]>
    );

    // 2. Populate the map with existing rooms of the target level
    // {
    //     BEDROOM: [{ id: 'room-1' }, { id: 'room-2' }],
    //     BATHROOM: [{ id: 'room-3' }, { id: 'room-4' }],
    //     KITCHEN: [{ id: 'room-5' }],
    //     [...]
    // }
    targetLevel.roomsData.forEach(room => {
        roomsByType[room.type].push(room);
    });

    // 3. For each room type, create a new array of rooms based on the desired count:
    //  a) If desired count is less than existing rooms count, take only a portion of existing rooms and discard the rest
    //  b) If desired count is greater than existing rooms count, create new rooms and add to the array
    const updatedTargetRooms: RoomListItem[] = [];
    Object.values(RoomType).forEach(roomType => {
        const desiredCount = Math.max(0, targetRoomCounts[roomType] ?? 0);
        const existingRooms = roomsByType[roomType];

        // 3a. Take only a portion of existing rooms and discard the rest if desired count is less than existing rooms count
        updatedTargetRooms.push(...existingRooms.slice(0, desiredCount));

        if (desiredCount > existingRooms.length) {
            // 3b. Create new rooms and add to the array if desired count is greater than existing rooms count
            const roomsToCreate = desiredCount - existingRooms.length;

            for (let i = 0; i < roomsToCreate; i += 1) {
                updatedTargetRooms.push({
                    id: createRoomId(),
                    name: createRoomName(roomType),
                    type: roomType,
                    height: defaultRoomHeight,
                    isHeated: DEFAULT_IS_HEATED,
                    isCooled: DEFAULT_IS_COOLED
                });
            }
        }
    });

    // 4. Create a new levels array with the updated level's roomsData for the target level
    return levels.map(level => (level.id === event.levelId ? { ...level, roomsData: updatedTargetRooms } : { ...level }));
};

export const deleteRoom = (levels: Level[], roomId: string): Level[] | null => {
    let hasRemovedRoom = false;

    const updatedLevelsArray = levels.map(level => {
        const filteredRooms = level.roomsData.filter(room => room.id !== roomId);
        if (filteredRooms.length !== level.roomsData.length) {
            hasRemovedRoom = true;
        }

        return {
            ...level,
            roomsData: filteredRooms
        };
    });

    return hasRemovedRoom ? updatedLevelsArray : null;
};

export const deleteLevel = (levels: Level[], levelId: string): Level[] | null => {
    const targetLevel = levels.find(level => level.id === levelId);
    if (!targetLevel) {
        return null;
    }

    let updatedLevelsArray = levels.filter(level => level.id !== levelId).map(level => ({ ...level }));

    const isTargetLevelAnUpperLevel = targetLevel.number >= 0;
    if (isTargetLevelAnUpperLevel) {
        updatedLevelsArray = updatedLevelsArray.map(level => {
            if (level.number > targetLevel.number) {
                return { ...level, number: level.number - 1 };
            }
            return level;
        });
    } else {
        updatedLevelsArray = updatedLevelsArray.map(level => {
            if (level.number < targetLevel.number) {
                return { ...level, number: level.number + 1 };
            }
            return level;
        });
    }

    return updatedLevelsArray;
};

export const toggleHeatingAndCooling = (
    levels: Level[],
    roomId: string,
    options: { heating?: boolean; cooling?: boolean }
): Level[] | null => {
    let hasUpdatedRoom = false;

    const updatedLevelsArray = levels.map(level => {
        const updatedRoomsData = level.roomsData.map(room => {
            if (room.id === roomId) {
                hasUpdatedRoom = true;
                return {
                    ...room,
                    isHeated: options.heating ?? room.isHeated,
                    isCooled: options.cooling ?? room.isCooled
                };
            }
            return room;
        });

        return { ...level, roomsData: updatedRoomsData };
    });

    return hasUpdatedRoom ? updatedLevelsArray : null;
};

export const updateCalculationSummary = (
    totalLivingArea: number,
    includeTotalBuilding: boolean,
    levels: Level[] | null,
    buildingLevelLoad?: { heatLoad: number; coolingLoad: number }
): CalculationSummary | undefined => {
    if (!levels) return undefined;

    const { totalHeatLoad, totalCoolingLoad, usedArea } = extractCalculationSummary(levels);
    const leftoverArea = totalLivingArea - usedArea;

    return {
        totalLivingArea,
        totalHeatLoad: totalHeatLoad + (buildingLevelLoad?.heatLoad ?? 0),
        totalCoolingLoad: totalCoolingLoad + (buildingLevelLoad?.coolingLoad ?? 0),
        leftoverArea,
        includeTotalBuilding
    };
};

export const extractCalculationSummary = (levels: Level[]): { totalHeatLoad: number; totalCoolingLoad: number; usedArea: number } => {
    const { totalHeatLoad, totalCoolingLoad, usedArea } = levels.reduce(
        (acc, level) => {
            level.roomsData.forEach(room => {
                if (room.isHeated) {
                    acc.totalHeatLoad += room.heatingLoad || 0;
                }

                if (room.isCooled) {
                    acc.totalCoolingLoad += room.coolingLoad || 0;
                }

                acc.usedArea += room.area || 0;
            });
            return acc;
        },
        { totalHeatLoad: 0, totalCoolingLoad: 0, usedArea: 0 }
    );

    return { totalHeatLoad, totalCoolingLoad, usedArea };
};
